/* eslint-disable react/jsx-props-no-spreading */
import React from "react";
import {
  Typography,
  Paper,
  makeStyles,
  Theme,
  createStyles,
} from "@material-ui/core";
import { EditComponentProps } from "material-table";
import {
  EditableTable,
  ColumnBaseHeaderStyle,
  ColumnBaseCellStyle,
  TextEditComponent,
} from "../../../../components";
import {
  UserConfigDataRow,
  UserSyslogConfigDataRow,
  UserSyslogConfigDbRow,
  ConnectionConfigDbRow,
} from "../../../../types/config";
import { ConnectionsApi, UserSyslogConfigsApi } from "../../../../helpers";
import { UserSyslogConfigHelper } from "./helpers/user-syslog-config-helper";

/** スタイル定義 */
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    undefined: {
      color: theme.palette.error.dark,
    },
  })
);

interface Props {
  parentData: UserConfigDataRow;
  onChange?: () => void;
  onError: (error: any) => void;
}

/** ユーザーシスログ設定テーブル */
export function UserSyslogConfigTable(props: Props) {
  const classes = useStyles();

  const { parentData, onChange, onError } = props;

  // 接続先設定データ
  const [data, setData] = React.useState<UserSyslogConfigDataRow[]>([]);

  React.useEffect(() => {
    let cleanedUp = false;

    if (parentData.id) {
      const fetchData = async () => {
        try {
          // 接続先設定すべて、ユーザーシスログ設定を取得する
          const resConnections: any = await ConnectionsApi.list();
          const resUserSyslogConfigs: any = await UserSyslogConfigsApi.list(
            parentData.id
          );

          // 接続先設定とユーザーシスログ設定を対応付ける
          // 対応するユーザーシスログ設定が無ければ、undefined を設定する
          const initialData = resConnections.data.map(
            (connectionRow: ConnectionConfigDbRow) => {
              const matchedSyslogConfig = resUserSyslogConfigs.data.find(
                (userSyslogConfigRow: UserSyslogConfigDbRow) => {
                  return userSyslogConfigRow.connection === connectionRow.id;
                }
              );
              return {
                id: matchedSyslogConfig ? matchedSyslogConfig.id : -1,
                connection: {
                  id: connectionRow.id,
                  name: connectionRow.name,
                  host: connectionRow.host,
                  path: connectionRow.path,
                },
                username: matchedSyslogConfig
                  ? matchedSyslogConfig.username
                  : "",
              };
            }
          );

          if (!cleanedUp) {
            setData(initialData);
          }
        } catch (error: any) {
          if (!cleanedUp) {
            onError(error);
          }
        }
      };
      fetchData();
    }

    return function cleanup() {
      cleanedUp = true;
    };
  }, []);

  // [Editable] (作成、編集、削除)
  // update
  const getRowUpdateHandler = (
    state: UserSyslogConfigDataRow[],
    setState: React.Dispatch<UserSyslogConfigDataRow[]>
  ) => {
    return (
      newData: UserSyslogConfigDataRow,
      oldData?: UserSyslogConfigDataRow | undefined
    ) =>
      new Promise<void>((resolve, reject) => {
        if (oldData == null) {
          reject();
          return false;
        }
        // API Access
        if (parentData.id) {
          if (newData.id && newData.id > 0) {
            // 更新
            const newDbRow = UserSyslogConfigHelper.toDbRow(
              parentData.id,
              newData
            );
            UserSyslogConfigsApi.update(oldData.id as number, newDbRow)
              // 成功
              .then(() => {
                resolve();
                // Stateを更新する
                const innerData = [...state];
                innerData[innerData.indexOf(oldData)] = newData;
                setState(innerData);
                return true;
              })
              // 失敗
              .catch((error: any) => {
                reject(); // keep open row editable
                onError(error);
                return false;
              });
          } else {
            // 作成
            const newDbRow = UserSyslogConfigHelper.toDbRow(
              parentData.id,
              newData
            );
            if (newDbRow.username === "") {
              // 何もせずに終了する（未設定のまま）
              resolve();
              return true;
            }
            UserSyslogConfigsApi.create(newDbRow)
              // 成功
              .then(() => {
                resolve();
                // Stateを更新する
                const innerData = [...state];
                innerData[innerData.indexOf(oldData)] = newData;
                setState(innerData);

                // 更新通知
                if (onChange) {
                  onChange();
                }
                return true;
              })
              // 失敗
              .catch((error: any) => {
                reject(); // keep open row editable
                onError(error);
                return false;
              });
          }
        }
        return true;
      });
  };

  //--------------------
  // コンポーネント
  return (
    <div style={{ maxWidth: "100%" }}>
      <EditableTable<UserSyslogConfigDataRow>
        title={(
          <Typography
            component="h6"
            style={{
              padding: "0.5rem",
              fontSize: "0.8rem",
              fontWeight: "bold",
            }}
          >
          ユーザーシスログ設定
          </Typography>
        )}
        columns={[
          {
            title: "接続先",
            field: "connection",
            editable: "never",
            headerStyle: ColumnBaseHeaderStyle,
            cellStyle: ColumnBaseCellStyle,
            defaultSort: "asc",
            customSort: (
              a: UserSyslogConfigDataRow,
              b: UserSyslogConfigDataRow
            ) => {
              // a, b => -1 (0未満) の場合、... a, b, ... の順になる
              // a, b => 0 の場合、並び替えない
              // a, b => 1 (0より大きい)の場合、 ... b, a, ... の順になる
              const nameA =
                Object.keys(a.connection).length > 0 && "name" in a.connection
                  ? a.connection.name
                  : "";
              const nameB =
                Object.keys(b.connection).length > 0 && "name" in b.connection
                  ? b.connection.name
                  : "";

              // "test".localeCompare("223 NAS") => 1
              // "223 NAS".localeCompare("test") => -1
              // "test".localeCompare("test") => 0
              return nameA.localeCompare(nameB);
            },
            render: function renderConnectionColumn(
              rowData: UserSyslogConfigDataRow
            ) {
              let displayName = `${rowData.connection.name} (${rowData.connection.host})`;
              if (rowData.connection.path) {
                displayName = `${rowData.connection.name} (${rowData.connection.host} ${rowData.connection.path})`;
              }
              return <span>{displayName}</span>;
            },
          },
          {
            title: "接続先ユーザー名",
            field: "username",
            headerStyle: ColumnBaseHeaderStyle,
            cellStyle: ColumnBaseCellStyle,
            sorting: false,
            render: function renderConnectionColumn(
              rowData: UserSyslogConfigDataRow
            ) {
              let component;
              if (rowData.username === "") {
                component = <span className={classes.undefined}>未設定</span>;
              } else {
                component = <span>{rowData.username}</span>;
              }
              return component;
            },
            editComponent: function editComponentUsernameColumn(
              fieldProps: EditComponentProps<UserSyslogConfigDataRow>
            ) {
              return <TextEditComponent {...fieldProps} autoFocus />;
            },
          },
        ]}
        data={data}
        editable={{
          onRowUpdate: getRowUpdateHandler(data, setData),
        }}
        // validate={(values: UserSyslogConfigDataRow) => {
        //   const errors: { [k: string]: any } = {};
        //   if (!values.username) {
        //     errors.username = "入力してください";
        //   }
        //   return errors;
        // }}
        components={{
          Container: function customContainer(componentProps: any) {
            return <Paper elevation={0}>{componentProps.children}</Paper>;
          },
        }}
        options={{
          showTitle: true,
        }}
      />
    </div>
  );
}
